import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import Icon from '../AppIcon';
import { useLocalStorage } from '../../hooks/useLocalStorage';

const OnboardingGuide = ({ userProfile }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [showGuide, setShowGuide] = useState(false);
  // Use user-specific localStorage key to prevent data leaking between users
  const storageKey = userProfile?.id ? `completed_onboarding_${userProfile.id}` : 'completed_onboarding';
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useLocalStorage(storageKey, false);
  
  // Check if user is new or needs onboarding
  useEffect(() => {
    // Don't show guide if user has completed onboarding
    if (hasCompletedOnboarding) return;
    
    // Don't proceed if userProfile is not loaded yet
    if (!userProfile) return;
    
    // Check if this is a new user (created within last 7 days)
    const isNewUser = userProfile?.created_at ? 
      (new Date() - new Date(userProfile.created_at) < 7 * 24 * 60 * 60 * 1000) : false;
    
    // Calculate profile completion percentage based on filled fields
    let filledFields = 0;
    let totalFields = 0;
    
    const profileFields = [
      'full_name',
      'bio',
      'skills',
      'phone',
      'location',
      'website',
      'linkedin',
      'hourly_rate',
      'experience_years',
      'profile_picture'
    ];
    
    profileFields.forEach(field => {
      totalFields++;
      const value = userProfile[field];
      if (value !== null && value !== undefined && value !== '' && 
          (!Array.isArray(value) || value.length > 0)) {
        filledFields++;
      }
    });
    
    const profileCompletion = (filledFields / totalFields) * 100;
    
    // Don't show guide if profile is 100% complete
    if (profileCompletion === 100) {
      setHasCompletedOnboarding(true);
      return;
    }
    
    // Show guide for new users or users with incomplete profiles (< 60%)
    if (isNewUser || profileCompletion < 60) {
      setShowGuide(true);
    }
  }, [userProfile, hasCompletedOnboarding]);
  
  // Define onboarding steps
  const steps = [
    {
      title: 'Welcome to Your VA Dashboard!',
      content: 'This is your central hub for managing your virtual assistant career. Here you can track applications, view job recommendations, and manage your profile.',
      icon: 'Home',
      target: 'welcome-section',
      action: null
    },
    {
      title: 'Complete Your Profile',
      content: 'A complete profile gets 5x more views from employers! Add your skills, experience, rates, and professional information to attract better opportunities.',
      icon: 'User',
      target: 'profile-completion',
      action: () => navigate('/va-profile-settings')
    },
    {
      title: 'Check Your Performance Stats',
      content: 'Monitor your career metrics including active applications, profile views, match score, and response rate. Track your progress over time.',
      icon: 'BarChart',
      target: 'metrics-section',
      action: null
    },
    {
      title: 'Browse Recommended Jobs',
      content: 'Discover jobs tailored to your skills and experience. Apply directly to opportunities that match your career goals.',
      icon: 'Search',
      target: 'recommended-jobs',
      action: () => navigate('/job-search')
    },
    {
      title: 'Manage Your Applications',
      content: 'Track all your job applications in one place. See application status, employer responses, and next steps for each position you\'ve applied to.',
      icon: 'FileText',
      target: null,
      action: () => navigate('/applications')
    },
    {
      title: 'Prepare for Interviews',
      content: 'View scheduled interviews, prepare with guidance, and practice with sample questions. Keep track of interview dates and employer details.',
      icon: 'Calendar',
      target: null,
      action: () => navigate('/interviews')
    },
    {
      title: 'Build Your Skills',
      content: 'Add skills to boost your profile visibility and improve your job match score significantly.',
      icon: 'Award',
      target: null,
      action: () => navigate('/up-skills')
    },
    {
      title: 'Connect & Communicate',
      content: 'Message employers and other professionals through our secure messaging system. Build relationships and get real-time updates on your applications.',
      icon: 'MessageCircle',
      target: null,
      action: () => navigate('/messages')
    }
  ];

  // Handle next/previous step
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      const nextStep = steps[currentStep + 1];

      // If the step has an explicit navigation action, run it (takes precedence)
      if (nextStep.action) {
        nextStep.action();
        return;
      }

      // Otherwise, if the step targets an on-page section, try to scroll to it.
      if (nextStep.target) {
        // If the element exists on the current page, scroll to it.
        const attemptScroll = () => {
          const targetElement = document.getElementById(nextStep.target);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return true;
          }
          return false;
        };

        if (!attemptScroll()) {
          // If not present (user navigated away), navigate to the dashboard then scroll after a short delay
          navigate('/va-dashboard');
          setTimeout(() => {
            attemptScroll();
          }, 250);
        }
      }
    } else {
      // Complete onboarding
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      const prevStep = steps[currentStep - 1];

      // If previous step has an explicit navigation action, run it
      if (prevStep.action) {
        prevStep.action();
        return;
      }

      // Otherwise, try to scroll to the target section (navigating to dashboard if necessary)
      if (prevStep.target) {
        const attemptScroll = () => {
          const targetElement = document.getElementById(prevStep.target);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return true;
          }
          return false;
        };

        if (!attemptScroll()) {
          navigate('/va-dashboard');
          setTimeout(() => {
            attemptScroll();
          }, 250);
        }
      }
    }
  };

  // Mark onboarding as complete
  const handleComplete = () => {
    setHasCompletedOnboarding(true);
    setShowGuide(false);
  };

  // Skip onboarding
  const handleSkip = () => {
    setHasCompletedOnboarding(true);
    setShowGuide(false);
  };

  // Expose reset function for debugging/testing (dev only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.resetOnboarding = () => {
        setHasCompletedOnboarding(false);
        setShowGuide(true);
        setCurrentStep(0);
        console.log('Onboarding reset - modal should show now');
      };
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.resetOnboarding;
      }
    };
  }, [setHasCompletedOnboarding]);

  if (!showGuide) return null;

  return (
    <div className="fixed bottom-20 md:bottom-8 left-0 right-0 z-50 flex justify-center px-4">
      <div className="bg-white rounded-lg shadow-xl border border-primary-200 w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-primary to-accent px-6 py-4 flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Icon name={steps[currentStep].icon} size={20} className="text-white" />
            {steps[currentStep].title}
          </h3>
          <button 
            onClick={handleSkip} 
            className="text-white hover:text-white/80 transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>
        
        <div className="p-6">
          <p className="mb-6 text-text-primary">{steps[currentStep].content}</p>
          
          {/* Action Button for Steps with Navigation */}
          {/* {steps[currentStep].action && (
            <div className="mb-6">
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={steps[currentStep].action}
                iconName="ArrowUpRight"
                iconPosition="right"
                className="w-full"
              >
                Go to {steps[currentStep].title.split(' ').slice(0, 2).join(' ')}
              </Button>
            </div>
          )} */}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {steps.map((_, index) => (
                <div 
                  key={index} 
                  className={`h-2 rounded-full transition-all ${
                    index === currentStep ? 'w-6 bg-primary' : 'w-2 bg-gray-300'
                  }`}
                />
              ))}
            </div>
            
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handlePrevious}
                  iconName="ArrowLeft"
                >
                  Back
                </Button>
              )}
              
              <Button 
                variant="primary" 
                size="sm" 
                onClick={handleNext}
                iconName={currentStep < steps.length - 1 ? "ArrowRight" : "Check"}
                iconPosition={currentStep < steps.length - 1 ? "right" : "left"}
              >
                {currentStep < steps.length - 1 ? 'Next' : 'Got It!'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingGuide;
